import React, { useState, useEffect } from "react";
import Notification from "../components/Notification";
import { useEventos } from "../context/EventosContext";
import Sidebar from "../components/Sidebar";
import HeaderPage from "../components/HeaderPage";
import Tarjetas from "../components/Tarjetas";
import CalendarHeader from "../components/Eventos/CalendarHeader";
import CalendarWeek from "../components/Eventos/CalendarWeek";
import CalendarDay from "../components/Eventos/CalendarDay";
import Modal from "../components/Eventos/CreateModal"; // Modal de creación
import DetallesModal from "../components/Eventos/DetallesModal"; // Modal de detalles
import { useAuth } from "../context/AuthContext";
import { useUsers } from "../context/UsersContext";

const Eventos = () => {
  const { eventos, loading, fetchEventosPorID, fetchEventos } = useEventos();
  const { salones, fetchSalones } = useUsers(); // Obtenemos los salones y la función para cargarlos
  const { user } = useAuth(); // Obtenemos el usuario autenticado

  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetallesModalOpen, setIsDetallesModalOpen] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSalon, setSelectedSalon] = useState(""); // Filtro por salón

  const defaultColors = ["#3498db", "#e74c3c", "#f1c40f", "#8e44ad"];
  const [eventosFiltrados, setEventosFiltrados] = useState(eventos);
  
  useEffect(() => {
    fetchSalones(); // Cargar los salones cuando se monte el componente
  }, []);

  useEffect(() => {
    let eventosConColores = eventos.map((evento, index) => {
      let fechaValida = "1970-01-01"; // Fecha por defecto si la entrada es inválida

      try {
        if (evento.fecha && typeof evento.fecha === "string") {
          const fechaISO = new Date(evento.fecha);
          if (!isNaN(fechaISO.getTime())) {
            fechaValida = fechaISO.toISOString().split("T")[0]; // Si es válida, la formatea
          } else {
            // console.warn(`Fecha inválida para el evento ID ${evento.evento_id}: ${evento.fecha}`);
          }
        } else {
          // console.warn(`Fecha inválida para el evento ID ${evento.evento_id}: ${evento.fecha}`);
        }
      } catch (error) {
        // console.warn(`Fecha inválida para el evento ID ${evento.evento_id}: ${evento.fecha}`);
      }

      return {
        ...evento,
        fecha: fechaValida, // Asignar la fecha procesada o por defecto
        color: evento.color || defaultColors[index % defaultColors.length],
      };
    });

    // Filtrar eventos por salon_id o salon_nombre
    if (selectedSalon && selectedSalon !== "Todos los Salones") {
      eventosConColores = eventosConColores.filter(
        (evento) => evento.salon_nombre === selectedSalon
      );
    }
    // console.log("Eventos después de filtrar:", eventosConColores);

    setEventosFiltrados(eventosConColores);
  }, [eventos, selectedSalon]);

  // Manejo del mes (avanzar o retroceder)
  const handleMonthChange = (newMonth) => {
    if (newMonth < 0) {
      setMonth(11); // Volver a diciembre
      setYear((prev) => prev - 1); // Retroceder al año anterior
    } else if (newMonth > 11) {
      setMonth(0); // Ir a enero
      setYear((prev) => prev + 1); // Avanzar al siguiente año
    } else {
      setMonth(newMonth); // Simplemente cambiar el mes
    }
  };

  // Obtener días y manejar eventos del calendario
  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDayOfMonth = getFirstDayOfMonth(month, year);
    const calendarDays = [];

    // Celdas vacías al inicio (para alinear correctamente el primer día)
    for (let i = 0; i < firstDayOfMonth; i++) {
      calendarDays.push(
        <div key={`empty-start-${i}`} className="p-2 sm:p-4"></div>
      );
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const fechaActual = new Date(year, month, day)
        .toISOString()
        .split("T")[0];
      const eventosDelDia = eventosFiltrados.filter(
        (evento) => evento.fecha === fechaActual
      );

      calendarDays.push(
        <CalendarDay
          key={day}
          day={day}
          month={month}
          year={year}
          eventosDelDia={eventosDelDia}
          selectedDate={selectedDate}
          onDateClick={setSelectedDate}
          onClick={() => handleDateClick(day)}
        />
      );
    }

    // Rellenar celdas vacías al final para completar la cuadrícula
    const totalCells = firstDayOfMonth + daysInMonth;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);

    for (let i = 0; i < remainingCells; i++) {
      calendarDays.push(
        <div key={`empty-end-${i}`} className="p-2 sm:p-4"></div>
      );
    }

    return calendarDays;
  };

  const handleCardClick = async (eventoId) => {
    try {
      const response = await fetchEventosPorID(eventoId);
      if (response && response.length > 0) {
        setSelectedEvento(response[0]);
        setIsDetallesModalOpen(true);
      } else {
        setNotification({
          type: "error",
          message: "No se encontraron detalles para este evento.",
        });
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: "No se encontraron detalles para este evento.",
      });
    }
  };

  //  Función para filtrar eventos por fecha seleccionada
  const handleDateClick = (day) => {
    const selectedDate = new Date(year, month, day).toISOString().split("T")[0];
    setSelectedDate(selectedDate); // Guardamos solo la fecha seleccionada
  };

  //  Función para restablecer la lista completa de eventos
  const handleResetEventos = async () => {
    setSelectedDate(null); // Restablecer fecha seleccionada
    setSelectedSalon(""); // Resetear filtro de salón
    const response = await fetchEventos();
    if (response && response.length > 0) {
      setEventosFiltrados(response);
    }
    // setEventosFiltrados(eventos); // Restaurar eventos originales
  };

  const closeNotification = () => {
    setNotification(null);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 p-2 sm:p-4 md:p-6 overflow-x-hidden">
        <HeaderPage />
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-4">
          <div className="flex-1 bg-white rounded-2xl lg:rounded-3xl shadow-md w-full overflow-hidden">
            <div className="max-w-5xl mx-auto p-2 sm:p-4">
              <CalendarHeader
                months={[
                  "Enero",
                  "Febrero",
                  "Marzo",
                  "Abril",
                  "Mayo",
                  "Junio",
                  "Julio",
                  "Agosto",
                  "Septiembre",
                  "Octubre",
                  "Noviembre",
                  "Diciembre",
                ]}
                month={month}
                year={year}
                showMonthDropdown={showMonthDropdown}
                setShowMonthDropdown={setShowMonthDropdown}
                showYearDropdown={showYearDropdown}
                setShowYearDropdown={setShowYearDropdown}
                handleMonthChange={handleMonthChange}
                handleYearChange={setYear}
                setIsModalOpen={setIsModalOpen}
                user={user}
                selectedSalon={selectedSalon}
                setSelectedSalon={setSelectedSalon}
                salones={salones} // Pasamos la lista de salones dinámicamente
              />
              <CalendarWeek />
              <div className="grid grid-cols-7 gap-1 sm:gap-2 md:gap-4 auto-rows-fr">
                {renderCalendarDays()}
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/4 min-w-0">
            {/*  Botón para restablecer eventos */}
            <button
              onClick={handleResetEventos}
              className="mb-4 w-full bg-main-blue text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-800 transition text-sm sm:text-base"
            >
              Ver todos los eventos
            </button>
            {loading ? (
              <p className="text-sm sm:text-base">Cargando eventos...</p>
            ) : (
              <div className="w-full overflow-hidden">
                <Tarjetas
                  eventos={selectedDate ? eventosFiltrados.filter((evento) => evento.fecha === selectedDate) : eventosFiltrados}
                  onCardClick={handleCardClick}
                />
              </div>
            )}
          </div>
        </div>

        {/* Modal de detalles */}
        <DetallesModal
          isOpen={isDetallesModalOpen}
          evento={selectedEvento}
          onClose={() => setIsDetallesModalOpen(false)}
        />

        {/* Modal de creación */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          showNotification={setNotification}
        />
      </main>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default Eventos;
